/**
 * Keeps an attempt's answers on the server while the student works.
 *
 * `saveAttempt` writes the whole answer set and the question the student is
 * on, replacing what was there. That makes every save idempotent, and it makes
 * order the one thing that can still go wrong: two saves in flight at once can
 * land in either order, and the older one landing second would put back an
 * answer the student has already changed. So there is never more than one
 * request in flight. A change made while one is out waits for it, and the next
 * request reads the answers as they are at the moment it is sent — the newest
 * state, whatever happened in between, and never a queue of stale ones.
 *
 * What the server has confirmed is counted by revision rather than by "the
 * last response said ok". Every change bumps the revision, a request remembers
 * the revision it was sent at, and its success confirms that revision and
 * nothing after it. A request that finishes late therefore cannot mark newer
 * answers as saved.
 *
 * No React and no Apollo here, so the ordering, the waits and the retry
 * schedule are tested with plain calls and fake timers. `useAttemptAutosave`
 * is what connects this to the mutation and to the page.
 */

/** Where the student's latest answers are. */
export type SaveStatus =
  /** On the server, as far as the server has said. */
  | 'saved'
  /** Changed since the last confirmed save, and not sent yet. */
  | 'unsaved'
  /** A request is on its way. */
  | 'saving'
  /** The last request failed. The answers are still in the page, and only there. */
  | 'failed'

export interface SaveFailure {
  /**
   * Whether the same save could succeed later. A dropped connection can; a
   * practice that has closed cannot, and asking again would only fail again.
   */
  retryable: boolean
  /** The server's reason, already in the reader's language, when it gave one. */
  message?: string
}

export type SaveOutcome = { ok: true } | ({ ok: false } & SaveFailure)

export interface SaverState {
  status: SaveStatus
  /**
   * The last failure, from the request that failed until a later one
   * succeeds — including while a retry is on its way, so a screen showing the
   * failure does not flicker every time it tries again.
   */
  failure: SaveFailure | null
}

/**
 * What changed, which decides how long to wait before saving it.
 *
 * A choice — picking an option, placing an item, matching a pair — is saved
 * almost at once. The short wait is there so a quick change of mind, A and
 * then C, goes as one request with C in it rather than two. Typing waits for a
 * pause, because a request per keystroke would be a request per letter. Moving
 * to another question is saved straight away.
 */
export type ChangeKind = 'choice' | 'typing' | 'move'

export const SAVE_DELAYS: Readonly<Record<ChangeKind, number>> = {
  choice: 300,
  typing: 1000,
  move: 0,
}

/**
 * The longest a change waits while more changes keep arriving.
 *
 * A student typing a long answer without ever pausing for a second would
 * otherwise push the save back for as long as they type.
 */
export const MAX_SAVE_WAIT = 5000

/**
 * How long to wait before trying a failed save again. The last value repeats,
 * so a student who is offline for an hour costs a request every thirty seconds
 * rather than a flood — and the connection coming back is `retry()`'s cue, from
 * the browser's `online` event, rather than something this has to guess.
 */
export const RETRY_DELAYS: readonly number[] = [2000, 4000, 8000, 15000, 30000]

export interface AnswerSaverOptions {
  /** Sends the answers as they are now. It reads them itself, at the moment of sending. */
  save: () => Promise<SaveOutcome>
  /** Called whenever the status or the failure changes. */
  onState?: (state: SaverState) => void
  /**
   * Read at the moment it matters rather than once, because the runner learns
   * whether a practice is timed only when the attempt arrives.
   *
   * `saveOnChange` off is a timed practice: it has always saved when the
   * student moves on rather than as they answer, and it keeps doing exactly
   * that. An empty `retryDelays` turns automatic retries off, which is also a
   * timed practice's existing behaviour — the next question saves again.
   */
  policy?: () => { saveOnChange: boolean; retryDelays: readonly number[] }
  delays?: Readonly<Record<ChangeKind, number>>
}

export interface AnswerSaver {
  getState: () => SaverState
  /** Whether everything the student did is confirmed on the server, with nothing on its way. */
  isSaved: () => boolean
  /** Something the server should hear about has changed. */
  changed: (kind: ChangeKind) => void
  /**
   * Save whatever is outstanding now, and resolve with whether all of it is on
   * the server. Resolves `false` at the first failure rather than waiting for a
   * retry, so the caller can say so and stay where it is.
   */
  flush: () => Promise<boolean>
  /** Try a failed save again now, rather than on the schedule. */
  retry: () => void
  /** Hold every request until `resume`: submission is on its way. */
  pause: () => void
  resume: () => void
  /** Stop scheduling anything. A request already on its way still finishes. */
  stop: () => void
  /** Undo `stop`, for a component that mounts, unmounts and mounts again. */
  start: () => void
}

export function createAnswerSaver(options: AnswerSaverOptions): AnswerSaver {
  const delays = options.delays ?? SAVE_DELAYS
  const policy = options.policy ?? (() => ({ saveOnChange: true, retryDelays: RETRY_DELAYS }))

  let revision = 0
  let confirmed = 0
  let sending = false
  // A save was asked for while one was already out. It goes as soon as that
  // one returns, and carries whatever the answers are by then.
  let sendAgain = false
  let failures = 0
  let paused = false
  let stopped = false
  let debounce: ReturnType<typeof setTimeout> | undefined
  let debounceSince: number | undefined
  let retryTimer: ReturnType<typeof setTimeout> | undefined
  let waiters: Array<(saved: boolean) => void> = []
  let state: SaverState = { status: 'saved', failure: null }

  const outstanding = () => confirmed < revision

  function publish(status: SaveStatus, failure: SaveFailure | null) {
    if (state.status === status && state.failure === failure) return
    state = { status, failure }
    options.onState?.(state)
  }

  function settle(saved: boolean) {
    const pending = waiters
    waiters = []
    for (const resolve of pending) resolve(saved)
  }

  function clearDebounce() {
    if (debounce !== undefined) clearTimeout(debounce)
    debounce = undefined
    debounceSince = undefined
  }

  function clearRetry() {
    if (retryTimer !== undefined) clearTimeout(retryTimer)
    retryTimer = undefined
  }

  function pump() {
    if (stopped || paused) return
    if (sending) {
      sendAgain = true
      return
    }
    if (!outstanding()) {
      publish('saved', null)
      settle(true)
      return
    }
    void send()
  }

  async function send() {
    clearDebounce()
    clearRetry()
    const sentAt = revision
    sending = true
    sendAgain = false
    publish('saving', state.failure)

    let outcome: SaveOutcome
    try {
      outcome = await options.save()
    } catch {
      // `save` is meant to turn every failure into an outcome. One that throws
      // anyway is treated as the connection it most likely is.
      outcome = { ok: false, retryable: true }
    }
    sending = false

    if (outcome.ok) {
      confirmed = Math.max(confirmed, sentAt)
      failures = 0
      if (!outstanding()) {
        publish('saved', null)
        settle(true)
        return
      }
      // Newer answers arrived while this one was out. They go now if a save
      // was asked for in the meantime — a choice's wait ran out, or somebody
      // is waiting on `flush` — and otherwise when their own wait runs out.
      publish('unsaved', null)
      if (sendAgain || waiters.length > 0) pump()
      return
    }

    failures += 1
    const failure: SaveFailure = {
      retryable: outcome.retryable,
      ...(outcome.message === undefined ? {} : { message: outcome.message }),
    }
    publish('failed', failure)
    settle(false)

    const { retryDelays } = policy()
    if (failure.retryable && retryDelays.length > 0 && !stopped && !paused) {
      const wait = retryDelays[Math.min(failures, retryDelays.length) - 1] ?? 0
      retryTimer = setTimeout(() => {
        retryTimer = undefined
        pump()
      }, wait)
    }
  }

  function changed(kind: ChangeKind) {
    if (stopped) return
    revision += 1
    if (state.status === 'saved') publish('unsaved', null)
    if (paused || !policy().saveOnChange) return
    // A failing save already has its next attempt scheduled, and that attempt
    // sends the answers as they are then, this change included. Sending now as
    // well would turn every tap on a dead connection into another request.
    if (state.failure) return

    // Each change restarts the wait, but never past `MAX_SAVE_WAIT` from the
    // first change still waiting.
    const now = Date.now()
    debounceSince ??= now
    const wait = Math.min(delays[kind], Math.max(0, debounceSince + MAX_SAVE_WAIT - now))
    if (debounce !== undefined) clearTimeout(debounce)
    debounce = setTimeout(() => {
      debounce = undefined
      debounceSince = undefined
      pump()
    }, wait)
  }

  function flush(): Promise<boolean> {
    clearDebounce()
    if (!outstanding() && !sending) return Promise.resolve(true)
    if (stopped) return Promise.resolve(false)
    const done = new Promise<boolean>((resolve) => waiters.push(resolve))
    pump()
    return done
  }

  function retry() {
    if (stopped || paused) return
    clearRetry()
    pump()
  }

  function pause() {
    paused = true
    clearDebounce()
    clearRetry()
  }

  function resume() {
    if (!paused) return
    paused = false
    // Anything held back goes now: a change made while submission was on its
    // way, or a `flush` that has been waiting for the hold to lift.
    if (waiters.length > 0 || (outstanding() && policy().saveOnChange)) pump()
  }

  function stop() {
    stopped = true
    clearDebounce()
    clearRetry()
  }

  function start() {
    stopped = false
  }

  return {
    getState: () => state,
    isSaved: () => !outstanding() && !sending,
    changed,
    flush,
    retry,
    pause,
    resume,
    stop,
    start,
  }
}
